import {
  postContentClass,
  zoomCaptionClass,
  zoomImgClass,
  zoomOverlayClass,
  zoomWrapperClass,
} from "./classes.js"

let initialized = false
let activeClose: ((immediate?: boolean) => void) | null = null

export function initImageZoom() {
  if (initialized) return
  initialized = true

  document.addEventListener("click", e => {
    const target = e.target as HTMLElement
    if (target.tagName !== "IMG") return

    if ((target as HTMLImageElement).dataset.zoom !== undefined) {
      e.preventDefault()
      openZoom(target as HTMLImageElement)
      return
    }

    const figure = target.closest('[data-type="image"]')
    if (!figure) return

    const contentEl = figure.closest(`.${postContentClass}`)
    if (!contentEl) return

    // Skip images inside the editor
    if (contentEl.getAttribute("contenteditable") === "true") return

    e.preventDefault()
    const title = getImageTitle(target as HTMLImageElement, figure)
    openZoom(target as HTMLImageElement, title)
  })
}

function openZoom(img: HTMLImageElement, title?: string) {
  if (activeClose) activeClose(true)

  let closed = false
  let hiRes: HTMLImageElement | null = null

  const rect = img.getBoundingClientRect()
  const computedStyle = getComputedStyle(img)
  const originalBorderRadius = computedStyle.borderRadius
  const originalCornerShape = computedStyle.getPropertyValue("corner-shape")

  const overlay = document.createElement("div")
  overlay.className = zoomOverlayClass

  // Wrapper handles position/size/border-radius animation with overflow:hidden.
  // The inner image always uses object-fit:cover, so as the wrapper transitions
  // from the source rect (matching its aspect ratio) to the fullscreen box
  // (matching the image's natural aspect ratio), the crop gradually opens up —
  // no objectFit switching needed and no visual snap.
  const wrapper = document.createElement("div")
  wrapper.className = zoomWrapperClass
  wrapper.style.top = `${rect.top}px`
  wrapper.style.left = `${rect.left}px`
  wrapper.style.width = `${rect.width}px`
  wrapper.style.height = `${rect.height}px`
  wrapper.style.borderRadius = originalBorderRadius
  if (originalCornerShape)
    wrapper.style.setProperty("corner-shape", originalCornerShape)

  const zoomedImg = document.createElement("img")
  zoomedImg.className = zoomImgClass
  zoomedImg.alt = img.alt || ""

  // Start with the already-loaded thumbnail so there's no blank frame,
  // then swap to the full-res version once it finishes loading.
  const currentSrc = img.currentSrc || img.src
  const fullResSrc = getFullResSrc(currentSrc)
  zoomedImg.src = currentSrc

  if (fullResSrc !== currentSrc) {
    hiRes = new Image()
    hiRes.src = fullResSrc
    hiRes.onload = () => {
      if (closed) return
      zoomedImg.src = fullResSrc
    }
  }

  wrapper.appendChild(zoomedImg)
  overlay.appendChild(wrapper)

  // Show caption if the image has a title
  let caption: HTMLElement | null = null
  if (title) {
    caption = document.createElement("div")
    caption.className = zoomCaptionClass
    caption.textContent = title
    overlay.appendChild(caption)
  }

  document.body.appendChild(overlay)
  document.body.style.overflow = "hidden"

  // Hide original image so it's not visible behind the overlay
  img.style.visibility = "hidden"

  // Force a layout flush so the initial styles on overlay and wrapper are
  // committed before the transition starts. Reading offsetHeight on the
  // overlay resolves its background and the wrapper's position/size,
  // giving the CSS transition a valid "from" state.
  void overlay.offsetHeight

  // Calculate target position (centered, fit viewport at natural aspect ratio).
  // On mobile, remove all padding so expanded images fill the viewport.
  // When a caption exists, measure its actual height (supports multiline)
  // and reserve only that space at the bottom.
  const isMobile = window.innerWidth <= 768
  const paddingInline = isMobile ? 0 : 32
  const paddingBlock = isMobile ? 0 : 32
  const captionSpace = caption ? caption.offsetHeight : 0
  const maxW = window.innerWidth - paddingInline * 2
  const maxH = window.innerHeight - paddingBlock * 2 - captionSpace
  const naturalW = img.naturalWidth || rect.width
  const naturalH = img.naturalHeight || rect.height
  const scale = Math.min(maxW / naturalW, maxH / naturalH)
  const targetW = naturalW * scale
  const targetH = naturalH * scale
  const targetX = (window.innerWidth - targetW) / 2
  const targetY = (window.innerHeight - captionSpace - targetH) / 2

  // Use double-rAF to guarantee the initial transparent frame is painted
  // before triggering the transition. A single rAF can still batch with the
  // append in some browsers; the nested rAF ensures a genuinely new frame.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (closed) return
      overlay.setAttribute("data-active", "")
      wrapper.style.top = `${targetY}px`
      wrapper.style.left = `${targetX}px`
      wrapper.style.width = `${targetW}px`
      wrapper.style.height = `${targetH}px`
      wrapper.style.borderRadius = "0px"
      caption?.setAttribute("data-active", "")
    })
  })

  // Close on scroll past threshold
  const scrollThreshold = 50
  let scrollAccumulated = 0

  function onWheel(e: WheelEvent) {
    scrollAccumulated += Math.abs(e.deltaY)
    if (scrollAccumulated >= scrollThreshold) close()
  }

  function close(immediate = false) {
    closed = true
    document.removeEventListener("keydown", onKeyDown)
    document.removeEventListener("wheel", onWheel)

    function cleanup() {
      if (hiRes) {
        hiRes.onload = null
        hiRes.src = ""
        hiRes = null
      }
      overlay.remove()
      img.style.visibility = ""
      if (activeClose === close) {
        document.body.style.overflow = ""
        activeClose = null
      }
    }

    if (immediate) {
      cleanup()
      return
    }

    // Stop intercepting clicks immediately so underlying images are
    // clickable again during the close animation.
    overlay.style.pointerEvents = "none"

    // Re-read rect in case the page scrolled
    const currentRect = img.getBoundingClientRect()

    overlay.removeAttribute("data-active")
    caption?.removeAttribute("data-active")
    wrapper.style.top = `${currentRect.top}px`
    wrapper.style.left = `${currentRect.left}px`
    wrapper.style.width = `${currentRect.width}px`
    wrapper.style.height = `${currentRect.height}px`
    wrapper.style.borderRadius = originalBorderRadius

    // Safety timeout in case transitionend doesn't fire (tab switch,
    // browser quirk, or no actual value change). 400ms > 300ms transition.
    const safetyTimeout = setTimeout(() => {
      overlay.removeEventListener("transitionend", onTransitionEnd)
      requestAnimationFrame(cleanup)
    }, 400)

    // Wait for the overlay's background-color to fully reach 0 before
    // removing it. This guarantees the final transparent frame is painted
    // first, preventing a blink from removing a still-compositing element.
    // pointer-events:none (set above) ensures clicks pass through during
    // the animation regardless of when cleanup fires.
    function onTransitionEnd(e: TransitionEvent) {
      if (e.propertyName !== "background-color") return
      clearTimeout(safetyTimeout)
      overlay.removeEventListener("transitionend", onTransitionEnd)
      requestAnimationFrame(cleanup)
    }
    overlay.addEventListener("transitionend", onTransitionEnd)
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape") close()
  }

  overlay.addEventListener("click", () => close())
  document.addEventListener("keydown", onKeyDown)
  document.addEventListener("wheel", onWheel, { passive: true })
  activeClose = close
}

/**
 * Extract the image title from the figure's figcaption or the img's title attribute.
 */
function getImageTitle(
  img: HTMLImageElement,
  figure: Element,
): string | undefined {
  const figcaption = figure.querySelector("figcaption")
  if (figcaption?.textContent) return figcaption.textContent
  if (img.title) return img.title
  return undefined
}

/**
 * If the src has a CDN class param (?class=postXXX), strip it for full
 * resolution. Otherwise return the src as-is.
 */
function getFullResSrc(src: string): string {
  try {
    const url = new URL(src)
    if (url.searchParams.has("class")) {
      url.searchParams.delete("class")
      return url.toString()
    }
  } catch {
    // Not a valid URL, return as-is
  }
  return src
}
